/**
 * W72 STEP 4 — THE PROGRESS CHART'S DRAWING, AS NUMBERS. What Swift Charts does for the phone, done here so the web
 * card draws the same picture: the sizes are `ExerciseProgressDetailView`'s own constants (plot 226 pt, the date band
 * 27 pt, 12 pt of end padding on the x scale, 6 pt of vertical plot padding, the 26 / 12 / 8 dot rings, the 2 pt
 * line), the curve is Swift Charts' `.monotone` (a monotone cubic — the Fritsch–Carlson / Steffen tangent form
 * d3 calls `curveMonotoneX`), the fill runs 14% → 2% of the accent, and the y ticks are "nice" numbers at the
 * requested count (1 · 2 · 5 × 10ⁿ). Pure; `progressChartGeometry.test.ts` executes it.
 *
 * ⚠️ WHAT IS NOT PROVEN AGAINST THE SWIFT, SAID: Swift Charts' own tick picker and curve are Apple's and cannot be
 * run here. His still (Weighted Pull-Up, 24/8 → 21/9, ticks 32 · 34 · 36) is the reference, and the tick rule is
 * pinned to reproduce it.
 */

export const PLOT_HEIGHT = 226;
export const DATE_BAND = 27;
/** W123 — 0: the window runs edge to edge (his: the first point on the axis line, the last at the gridlines' end); the
 *  12 px of W121 was room for a half label, and the two end labels now hang inward instead. */
export const END_PADDING = 0;
export const PLOT_V_PADDING = 6;
export const TICK = 4;
export const AXIS_GAP = 6;

/** "Nice" tick values inside [lo, hi] for about `count` ticks — steps of 1, 2 or 5 × 10ⁿ. */
export function niceTicks(lo: number, hi: number, count: number): number[] {
  if (!(hi > lo) || count < 1) return [];
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const err = raw / mag;
  const step = (err >= 7.07 ? 10 : err >= 3.16 ? 5 : err >= 1.41 ? 2 : 1) * mag;
  const out: number[] = [];
  const start = Math.ceil(lo / step - 1e-9);
  for (let k = start; k * step <= hi + 1e-9; k++) out.push(Number((k * step).toFixed(10)));
  return out;
}

/** An integral field ticks on whole numbers only — the phone asks for at most one tick per unit. */
export function yTicks(lo: number, hi: number, count: number, integral: boolean): number[] {
  const t = niceTicks(lo, hi, count);
  return integral ? t.filter((v) => Number.isInteger(v)) : t;
}

export type XY = { readonly x: number; readonly y: number };

/** A monotone cubic through the points (x strictly increasing) — `M … C …`, never overshooting between two dots. */
export function monotonePath(pts: readonly XY[]): string {
  const n = pts.length;
  if (n === 0) return "";
  const f = (v: number) => Number(v.toFixed(2));
  if (n === 1) return `M${f(pts[0]!.x)},${f(pts[0]!.y)}`;
  const dx: number[] = [], m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1]!.x - pts[i]!.x);
    m.push(dx[i]! === 0 ? 0 : (pts[i + 1]!.y - pts[i]!.y) / dx[i]!);
  }
  const t: number[] = new Array(n).fill(0);
  t[0] = m[0]!;
  t[n - 1] = m[n - 2]!;
  for (let i = 1; i < n - 1; i++) {
    const a = m[i - 1]!, b = m[i]!;
    if (a * b <= 0) { t[i] = 0; continue; }
    const h0 = dx[i - 1]!, h1 = dx[i]!;
    const p = (a * h1 + b * h0) / (h0 + h1);
    t[i] = (Math.sign(a) + Math.sign(b)) * Math.min(Math.abs(a), Math.abs(b), 0.5 * Math.abs(p));
  }
  let d = `M${f(pts[0]!.x)},${f(pts[0]!.y)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i]!, p1 = pts[i + 1]!, h = dx[i]! / 3;
    d += `C${f(p0.x + h)},${f(p0.y + h * t[i]!)},${f(p1.x - h)},${f(p1.y - h * t[i + 1]!)},${f(p1.x)},${f(p1.y)}`;
  }
  return d;
}

/** The same curve closed down to `baseline` — the soft fill under the line. */
export function areaPath(pts: readonly XY[], baseline: number): string {
  if (pts.length < 2) return "";
  const first = pts[0]!, last = pts[pts.length - 1]!;
  return `${monotonePath(pts)}L${Number(last.x.toFixed(2))},${baseline}L${Number(first.x.toFixed(2))},${baseline}Z`;
}

export type Frame = { width: number; plotLeft: number; plotRight: number; plotTop: number; plotBottom: number };

/** The plot's rectangle inside a card of `width`: the y-label column on the left, the date band below. */
export function frame(width: number, yLabelChars: number): Frame {
  const labelW = Math.ceil(yLabelChars * 6.6);
  const plotLeft = labelW + TICK + AXIS_GAP;
  return { width, plotLeft, plotRight: width, plotTop: 0, plotBottom: PLOT_HEIGHT - DATE_BAND };
}

export function xScale(fr: Frame, lower: number, upper: number) {
  const a = fr.plotLeft + END_PADDING, b = fr.plotRight - END_PADDING;
  const span = upper - lower || 1;
  return {
    at: (ms: number) => a + ((ms - lower) / span) * (b - a),
    invert: (x: number) => lower + ((x - a) / (b - a)) * span,
  };
}

export function yScale(fr: Frame, lower: number, upper: number) {
  const top = fr.plotTop + PLOT_V_PADDING, bottom = fr.plotBottom - PLOT_V_PADDING;
  const span = upper - lower || 1;
  return (v: number) => bottom - ((v - lower) / span) * (bottom - top);
}

/** Where the pan may rest: the window's leading edge, clamped inside the scale, snapped to a midnight on release. */
export function clampScroll(x: number, lower: number, upper: number, windowMs: number): number {
  return Math.min(Math.max(x, lower), Math.max(upper - windowMs, lower));
}
